import { useEditorParseData, VARIABLE_SHIELD } from 'components/FormulaEditor';
import type { ActKc2VolumeVariableSelectionItem } from 'gql/graphql';
import { Text } from 'uikit';

import { useCallback, useMemo, useState } from 'react';

import { useActFormulaData } from './useActFormulaData';

export const useActFormulaHelpers = (selectionListVars: any) => {
    const [dataSet, setDataSet] = useState<
        (ActKc2VolumeVariableSelectionItem & { originalName: string })[] | undefined
    >(undefined);

    const { isLoading, formula } = useActFormulaData({
        variables: selectionListVars,
    });

    const dataObjModifier = useCallback((income: ActKc2VolumeVariableSelectionItem[]) => {
        const nameCount: { [key: string]: number } = {};

        const modified = income.map((item) => {
            const originalName = item.name;

            if (nameCount[originalName] === undefined) {
                nameCount[originalName] = 1;

                return {
                    ...item,
                    originalName,
                };
            }
            nameCount[originalName]++;

            return {
                ...item,
                originalName,
                name: `${originalName}(${nameCount[originalName]})`,
            };
        });

        setDataSet(modified as (ActKc2VolumeVariableSelectionItem & { originalName: string })[]);

        return modified;
    }, []);

    const customSerializer = useCallback<(i: string) => string>(
        (item: string) => {
            const currentItem = dataSet?.find((i) => i.name === item);

            if (currentItem) {
                return `[@${currentItem.originalName}|REF:${currentItem.uniqueId}]`;
            }

            return item;
        },
        [dataSet],
    );

    const { deserializeFromBackend, serializeStringForBackend } = useEditorParseData({ customSerializer });

    const patchedFormula = useMemo(() => {
        if (!formula) {
            return '';
        }

        const temp = formula
            ?.split(VARIABLE_SHIELD)
            .map((chunk) => {
                const isVariable = VARIABLE_SHIELD.test(chunk);

                if (isVariable) {
                    const nakedString = chunk.substring(2, chunk.length - 1);
                    const [currentName, currentId] = nakedString.split('|REF:');
                    const correctName = dataSet?.find((item) => item.uniqueId === currentId)?.name;

                    if (correctName) {
                        return chunk.replace(currentName, correctName);
                    }

                    return `@${currentName}`;
                }

                return chunk;
            })
            ?.join('');

        return deserializeFromBackend(temp);
    }, [dataSet, deserializeFromBackend, formula]);

    const sourceDataGetter = useCallback(
        (item: ActKc2VolumeVariableSelectionItem & { entityName: string }) => ({
            entityName: item.name,
        }),
        [],
    );

    const getSelectItemLabel = useCallback(
        (item: string) => {
            const candidate = dataSet?.find((i) => i.name === item);

            return (
                <div>
                    {item}
                    {Boolean(candidate?.coefficients?.length) && (
                        <Text view="secondary" size="2xs">
                            ( {candidate?.coefficients?.join(', ')})
                        </Text>
                    )}
                </div>
            ) as any;
        },
        [dataSet],
    );

    return {
        sourceDataGetter,
        dataObjModifier,
        getSelectItemLabel,
        isLoading,
        isDataSetCompleted: Boolean(dataSet?.length),
        patchedFormula,
        serializeStringForBackend,
    };
};
